import React, { useState, useEffect } from 'react';
import { userQuotaAPI } from '../../services/userQuotaAPI';
import { toast } from 'react-hot-toast';

const ExtractionButton = ({ 
  selectedDocumentType, 
  documentTypes, 
  uploadedFiles, 
  uploading, 
  progress,
  onClick 
}) => {
  const [quotaCheck, setQuotaCheck] = useState({ sufficient: true, loading: false });

  const checkQuota = async () => {
    if (uploadedFiles.length === 0) return;
    
    setQuotaCheck({ sufficient: true, loading: true });
    
    try {
      const result = await userQuotaAPI.checkQuotaSufficient(uploadedFiles.length);
      
      if (result.success && result.data) {
        setQuotaCheck({ 
          sufficient: result.data.has_sufficient, 
          loading: false, 
          quotaInfo: {
            available: result.data.available_documents,
            needed: result.data.documents_needed,
            remaining: result.data.remaining_after
          }
        });
      } else {
        setQuotaCheck({ sufficient: false, loading: false });
      }
    } catch (error) {
      console.error('Error al verificar cuota:', error);
      setQuotaCheck({ sufficient: false, loading: false });
    }
  };

  useEffect(() => {
    checkQuota();
  }, [uploadedFiles.length]);

  const isDisabled = !selectedDocumentType || documentTypes.length === 0 || uploadedFiles.length === 0 || uploading || !quotaCheck.sufficient;
  
  const getTooltip = () => {
    if (!selectedDocumentType) return "Selecciona un tipo de documento para continuar";
    if (uploadedFiles.length === 0) return "Sube al menos un archivo para continuar";
    if (!quotaCheck.sufficient) return `No tienes suficientes documentos. Necesitas ${uploadedFiles.length}, pero solo tienes ${quotaCheck.quotaInfo?.available || 0} disponibles`;
    if (uploading) return "Procesando archivos...";
    return "";
  };

  const handleClick = async () => {
    // Verificar cuota antes de proceder
    const result = await userQuotaAPI.checkQuotaSufficient(uploadedFiles.length);
    
    if (!result.success || !result.data || !result.data.has_sufficient) {
      const available = result.data?.available_documents || 0;
      const needed = uploadedFiles.length;
      const shortage = needed - available;
      
      toast.error(
        `No tienes suficientes documentos. Necesitas ${needed}, pero solo tienes ${available} disponibles. ` +
        `Te faltan ${shortage} documentos.`
      );
      setQuotaCheck({ 
        sufficient: false, 
        loading: false, 
        quotaInfo: {
          available: available,
          needed: needed,
          shortage: shortage
        }
      });
      return;
    }

    onClick();
  };

  return (
    <div className="extraction-button-container">
      {/* Alerta de cuota insuficiente */}
      {!quotaCheck.sufficient && !quotaCheck.loading && uploadedFiles.length > 0 && (
        <div className="alert alert-warning py-2 px-3 mb-3">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            <small>
              <strong>Cuota insuficiente:</strong> Necesitas {uploadedFiles.length} documentos, pero solo tienes {quotaCheck.quotaInfo?.available || 0} disponibles.
            </small>
          </div>
          <div className="mt-2">
            <a href="/comprar-documentos" className="btn btn-sm btn-warning">
              <i className="bi bi-plus-circle me-1"></i>
              Comprar más documentos
            </a>
          </div>
        </div>
      )}

      <button 
        className={`btn btn-lg w-100 shadow-sm ${
          uploading ? 'btn-success' : isDisabled ? 'btn-secondary' : 'btn-success'
        }`}
        disabled={isDisabled}
        onClick={handleClick}
        title={getTooltip()}
      >
        {uploading ? (
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex align-items-center mb-1">
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              <span className="fw-semibold">
                {progress.totalFiles > 0
                  ? `Procesando archivo ${progress.processedFiles + 1} de ${progress.totalFiles}`
                  : 'Procesando...'}
                {progress.totalFiles > 1 && (
                  <span className="ms-2 small">· {progress.progress}%</span>
                )}
              </span>
            </div>
            {progress.currentFile && (
              <small className="text-truncate" style={{ maxWidth: '220px' }}>
                {progress.currentFile}
              </small>
            )}
          </div>
        ) : (
          <>
            <i className="bi bi-play-circle-fill me-2"></i>
            <span>Iniciar Extracción</span>
            {uploadedFiles.length > 0 && (
              <span className="badge bg-light text-dark ms-2">{uploadedFiles.length}</span>
            )}
          </>
        )}
      </button>
      
      {uploading && progress.progress > 0 && (
        <div className="progress" style={{ height: '6px' }}>
          <div 
            className="progress-bar progress-bar-striped progress-bar-animated bg-success" 
            role="progressbar" 
            style={{ width: `${progress.progress}%` }}
            aria-valuenow={progress.progress}
            aria-valuemin="0" 
            aria-valuemax="100"
          >
          </div>
        </div>
      )}      
     
    </div>
  );
};

export default ExtractionButton;
